import React, { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import Reveal from 'react-reveal/Reveal';

import { useUrl } from '../hooks'

export const Background = styled.div`
    background-color: ${p => p.dark ? p.theme.dark : p.primary ? p.theme.primary : p.medium ? p.theme.medium : p.theme.light};
    ${p => p.shadow && css`
        box-shadow: ${p => p.theme.shadow};
    `}

    height: 100%;
`

export const Text = styled.div`
    box-sizing: border-box;
    font-size: ${p => p.H1 ? '2rem' : p.H2 ? '1rem' : p.H12 ? '1.5rem' : '.8rem'};
    font-weight: ${p => p.bold ? 'bold' : 'normal'};
    color: ${p => p.dark ? p.theme.dark : p.primary ? p.theme.primary : p.medium ? p.theme.medium : p.theme.light};
    height: 100%;
    white-space: ${p => p.oneline ? 'nowrap' : 'pre-line'};
    
    ${p => p.center && css`
        text-align: center;
    `}

    ${p => p.onClick && css`
        cursor: pointer;
        :hover {
            color: ${p => p.theme.primary};
        }
    `}

    ${p => p.middle && css`
        display: flex;
        align-items: center;

        ${p => p.center && css`
            justify-content: center;
        `}
    `}

    ${p => p.bottom && css`
        display: flex;
        align-items: flex-end;

        ${p => p.center && css`
            justify-content: center;
        `}
    `}
`

export const SVG = styled.div`
    mask-image: url(${p => p.image});
    mask-size: ${p => p.contain ? 'contain' : 'cover'};
    mask-position: center;
    mask-repeat: no-repeat;
    background-color: ${p => p.dark ? p.theme.dark : p.primary ? p.theme.primary : p.gray ? p.theme.gray : p.theme.light};
    height: 100%;

    ${p => p.onClick && css`
        cursor: pointer;
        :hover { background-color: ${p.theme.primary}; }
    `}
`

const ImageContainer = styled.div`
    box-sizing: border-box;
    background-image: url(${p => p.image}), url(${p => p.placeholder});
    background-size: ${p => p.contain ? 'contain' : p.fit ? '100% 100%' : 'cover'};
    background-repeat: no-repeat;
    background-position: ${p => p.top ? 'top' : 'center'};
    height: 100%;
`

export function Image(props) {
	const url = useUrl(props.url)
	return (
		<ImageContainer
			style={props.style}
			top={props.top}
			image={url ? url : props.image}
			contain={props.contain}
			fit={props.fit}
		> {props.children} </ ImageContainer>
	)
}

export function Animation(props) {
	const [current, setCurrent] = useState(0)

	const setNext = () => {
		setCurrent(c => {
			let next = c + 1
			if (next >= props.images.length) next = 0
			return next
		})
	}

	useEffect(() => {
		const interval = setInterval(setNext, 40)
		return () => clearInterval(interval)
	}, props.images)

	return (
		<Image style={props.style} contain={props.contain} fir={props.fit} image={props.images[current]} />
	)
}

const ButtonContainer = styled.div`
	border-radius: ${p => p.theme.roundCorners};
    box-sizing: border-box;
    height: ${p => p.theme.minSize};
    background-color: ${p => p.dark ? p.theme.dark : p.light ? p.theme.light : p.gray ? p.theme.medium : p.theme.primary};

	${p => !p.disabled ? css`
		cursor: pointer;
		:hover {
			background-color: ${p => p.dark ? p.theme.darkLight : p.light ? p.theme.primary : p.theme.primaryHover};
			${Text} {
				color: ${p => p.theme.light};
			}
			${SVG} {
				background-color: ${p => p.theme.light};
			}
		}
	` : css`
		filter: brightness(90%);
		opacity: .6;
	`}

    ${Text} {
        display: flex;
        align-items: center;
        justify-content: center;
    }

`

const TextIconContianer = styled.div`
    height: 100%;
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: ${p => p.inverse ? 'auto 2rem' : '2rem auto'};
    box-sizing: border-box;

    ${p => p.selected && css`
        ${SVG} { background-color: ${p => p.theme.primary}; }
        ${Text} { color: ${p => p.theme.primary}; }
    `}

    ${p => p.interactable && css`
        cursor: pointer;
        :hover {
            ${SVG} { background-color: ${p => p.theme.primary}; }
            ${Text} { color: ${p => p.theme.primary}; }
        }
    `}
`

export function TextIcon(props) {
	return (
		<TextIconContianer selected={props.selected} onClick={props.onClick} interactable={props.onClick} inverse={props.inverse} style={props.style}>
			<Text oneline style={{ gridRow: '1/2', gridColumn: props.inverse ? '1/2' : '2/3' }} H2 bold>{props.children}</Text>
			<SVG style={{ gridRow: '1/2', gridColumn: props.inverse ? '2/3' : '1/2' }} contain image={props.image} />
		</TextIconContianer>
	)
}

export function FadeInUp(props) {
	const [fire, setFire] = useState(false)
	const [init, setInit] = useState(true)

	if (init) {
		setInit(false)
		setTimeout(() => setFire(true), props.delay ? props.delay : 0)
	}

	return (
		<Reveal when={fire} effect='fadeInUp' duration={500}>
			{props.children}
		</Reveal>
	)
}

export function IconButton(props) {
	const style = props.style ? props.style : {}
	style.padding = '.5rem'

	return (
		<Button icon style={style} onClick={props.onClick} dark={props.dark} gray={props.gray} light={props.light}>
			<SVG style={{ boxSizing: 'border-box' }} contain image={props.image} primary={props.light} light={props.gray} />
		</Button>
	)
}

export function Button(props) {
	return (
		<ButtonContainer disabled={props.disabled} style={props.style} onClick={props.disabled ? () => { } : props.onClick} gray={props.gray} dark={props.dark} light={props.light}>
			{
				props.icon ? props.children :
					<Text style={{ whiteSpace: 'nowrap', margin: '0 1rem' }} center H2 bold primary={props.light}>
						{props.children}
					</Text>
			}
		</ButtonContainer>
	)
}

const InputFieldContainer = styled.input`
	border: .1rem solid ${p => p.theme.light};
    width: 100%;
    box-sizing: border-box;

    color: ${p => p.title ? p.theme.dark : p.theme.light};
    font-size: 1rem;

    height: 2rem;
    background-color: rgba(0,0,0,0);
    ${p => p.center && css`
        text-align: center;
    `}

    ::placeholder {
        color: ${p => p.title ? p.theme.dark : p.theme.light};
        opacity: 1;
    }
    /* padding: 0 1rem; */

    border-bottom: .1rem solid ${p => p.theme.dark};

`

const SInputField = styled.div`
    height: min-content;
`

export function InputField(props) {
	const inputRef = useRef()

	useEffect(() => {
		if (props.autoSelect && inputRef.current) inputRef.current.select()
	}, [inputRef.current])

	const inputField = <InputFieldContainer
		title={props.title}
		ref={inputRef}
		onKeyDown={props.onKeyDown}
		center={props.center}
		placeholder={props.placeholder}
		value={props.value ? props.value : ''}
		onChange={e => props.onChange(e.target.value)}
		type={props.type ? props.type : 'text'}
		style={props.style}
	/>

	return props.title ? <SInputField>
		<Text H4 bold dark>{props.title}</Text>
		{inputField}
	</SInputField> : inputField

}

const UploadFileHolder = styled.input`
    box-sizing: border-box;
    height: 100%;
    background-color: ${p => p.dark ? p.theme.dark : p.light ? p.theme.light : p.theme.primary};
    cursor: pointer;
    :hover {
        background-color: ${p => p.dark ? p.theme.darkLight : p.light ? p.theme.primary : p.theme.primaryHover};
    }
    color: white;
    font-weight: bold;
`

export function UploadFile(props) {
	const [state, setState] = useState()

	const handleChange = e => {
		if (e.target.files[0]) props.fileHanlder(e.target.files[0])
	}

	return (
		<UploadFileHolder
			style={props.style}
			type='file'
			onChange={handleChange}
		/>
	)
}

const AspectRatioContainer = styled.div`
    width: ${p => p.width ? p.width : '100%'};
    padding-top: ${p => p.paddingTop ? p.paddingTop : '100%'};
    position: relative;
    background-color: red;
`

const AspectRatioChild = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
`

export function AspectRatio(props) {
	return (
		<AspectRatioContainer width={props.width} paddingTop={props.padding}>
			<AspectRatioChild>
				{props.children}
			</AspectRatioChild>
		</AspectRatioContainer>
	)
}